import type { EntityRecord } from "@wedding/schemas";
import { Button } from "@wedding/ui";
import { Pencil, X } from "lucide-react";
import type { ModuleConfig } from "../config";

export type ProjectMemberIdentity = {
  id: string;
  email: string;
  userId?: string;
};

function actorName(userId: string, members: ProjectMemberIdentity[]) {
  if (userId === "dev-user") return "本地项目所有者（dev@local.test）";
  if (userId === "rsvp-public") return "宾客公开登记 / 回复";
  return (
    members.find((member) => member.userId === userId)?.email ?? "项目成员"
  );
}

const money = (value: number) =>
  new Intl.NumberFormat("zh-CN", {
    style: "currency",
    currency: "CNY",
    maximumFractionDigits: 2,
  }).format(value / 100);

export function EntityDetail({
  item,
  config,
  relationLabels,
  members,
  onClose,
  onEdit,
}: {
  item: EntityRecord;
  config: ModuleConfig;
  relationLabels: Record<string, Record<string, string>>;
  members: ProjectMemberIdentity[];
  onClose(): void;
  onEdit(): void;
}) {
  const dialogRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    dialogRef.current?.querySelector<HTMLElement>("button")?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = [
        ...dialogRef.current.querySelectorAll<HTMLElement>(
          "button:not(:disabled), [tabindex]:not([tabindex='-1'])",
        ),
      ];
      const first = focusable[0];
      const last = focusable.at(-1);
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      previous?.focus();
    };
  }, [onClose]);
  return (
    <div className="drawer-backdrop">
      <aside
        ref={dialogRef}
        className="drawer entity-detail"
        role="dialog"
        aria-modal="true"
        aria-label={`记录详情：${String(item.data[config.fields[0]?.key ?? "title"] ?? config.singular)}`}
      >
        <header>
          <div>
            <span className="eyebrow">记录详情</span>
            <h2 id="entity-detail-title">
              {String(
                item.data[config.fields[0]?.key ?? "title"] ?? config.singular,
              )}
            </h2>
          </div>
          <button
            className="icon-button"
            aria-label="关闭详情"
            onClick={onClose}
          >
            <X />
          </button>
        </header>
        <div className="detail-content">
          <dl className="detail-fields">
            {config.fields.map((field) => {
              const raw = item.data[field.key];
              const value =
                field.type === "relation"
                  ? (relationLabels[field.key]?.[String(raw)] ?? "未关联")
                  : field.type === "currency"
                    ? money(Number(raw ?? 0))
                    : raw === true
                      ? "是"
                      : raw === false
                        ? "否"
                        : Array.isArray(raw)
                          ? raw.join("、")
                          : String(raw ?? "未填写");
              return (
                <div key={field.key}>
                  <dt>{field.label}</dt>
                  <dd>{value || "未填写"}</dd>
                </div>
              );
            })}
          </dl>
          <dl className="detail-meta">
            <div>
              <dt>创建时间</dt>
              <dd>{new Date(item.createdAt).toLocaleString("zh-CN")}</dd>
            </div>
            <div>
              <dt>最近修改</dt>
              <dd>{new Date(item.updatedAt).toLocaleString("zh-CN")}</dd>
            </div>
            <div>
              <dt>最近修改人</dt>
              <dd>{actorName(item.updatedBy, members)}</dd>
            </div>
            {Boolean(item.data.sourceModule) && (
              <div>
                <dt>来源</dt>
                <dd>{String(item.data.sourceModule)}</dd>
              </div>
            )}
          </dl>
        </div>
        <footer>
          <Button className="secondary" onClick={onClose}>
            关闭
          </Button>
          <Button onClick={onEdit}>
            <Pencil size={16} /> 编辑这条记录
          </Button>
        </footer>
      </aside>
    </div>
  );
}
import { useEffect, useRef } from "react";
